import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Flag, Check, Undo2, RefreshCw, Sparkles, X, ExternalLink } from 'lucide-react'
import {
  getFlagsSummary, getFlags, acceptFlags, resetFlags, regenerateFlags, getModels,
} from '@/api/client'
import { AuthedImage } from '@/components/AuthedImage'
import { mediaPhotoUrl, mediaGeneratedUrl } from '@/lib/media'

export function Flagged() {
  const [summary, setSummary] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [state, setState] = useState('all')
  const [selected, setSelected] = useState(new Set())
  const [models, setModels] = useState([])
  const [showRegen, setShowRegen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [s, l] = await Promise.all([getFlagsSummary(), getFlags({ state, limit: 300 })])
      setSummary(s.data)
      setRows(l.data.generations || [])
      setSelected(new Set())
    } finally {
      setLoading(false)
    }
  }, [state])

  useEffect(() => { load(); getModels().then((r) => setModels(r.data || [])).catch(() => {}) }, [load])

  const allChecked = rows.length > 0 && selected.size === rows.length
  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.generation_id)))

  const run = async (fn, successMsg) => {
    setBusy(true)
    try {
      const r = await fn()
      setToast({ kind: 'ok', msg: successMsg(r.data) })
      await load()
    } catch (e) {
      setToast({ kind: 'err', msg: e?.response?.data?.detail || 'Action failed' })
    } finally {
      setBusy(false)
    }
  }

  const selectedIds = useMemo(() => [...selected], [selected])

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Flagged photos</h1>
          <p className="text-gray-500">Review what reviewers flagged, then regenerate or accept.</p>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="flag-state" value={state} onChange={(e) => setState(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="all">All flagged</option>
            <option value="pending_regen">Waiting to regenerate</option>
            <option value="regenerating">Regenerating</option>
          </select>
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <Stat label="Waiting to regenerate" value={summary.flagged_open} />
          <Stat label="Regenerating" value={summary.regenerating} />
          <Stat label="Categories affected" value={summary.by_category?.length ?? 0} />
        </div>
      )}

      {toast && (
        <div data-testid="flag-toast" className={`mb-4 rounded-lg px-4 py-3 text-sm border ${toast.kind === 'ok' ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-700'}`}>
          {toast.msg}
        </div>
      )}

      {selected.size > 0 && (
        <div className="sticky top-2 z-10 mb-4 bg-white shadow border border-gray-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700">{selected.size} selected</span>
          <button data-testid="flag-regenerate" onClick={() => setShowRegen(true)} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500 text-white text-sm hover:bg-orange-600 disabled:opacity-50">
            <Sparkles className="h-4 w-4" /> Regenerate
          </button>
          <button data-testid="flag-accept" onClick={() => run(() => acceptFlags(selectedIds), (d) => `Accepted ${d.accepted}`)} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-100 text-green-700 text-sm hover:bg-green-200 disabled:opacity-50">
            <Check className="h-4 w-4" /> Accept as-is
          </button>
          <button data-testid="flag-reset" onClick={() => run(() => resetFlags(selectedIds), (d) => `Sent ${d.reset} back to review`)} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200 disabled:opacity-50">
            <Undo2 className="h-4 w-4" /> Send back to review
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-24 text-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto" /></div>
      ) : rows.length === 0 ? (
        <div className="py-24 text-center text-gray-500">
          <Flag className="h-10 w-10 text-gray-300 mx-auto mb-3" /> No flagged photos.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-3 py-3 text-left w-8">
                  <input type="checkbox" data-testid="flag-select-all" checked={allChecked} onChange={toggleAll} />
                </th>
                <th className="px-3 py-3 text-left">Original</th>
                <th className="px-3 py-3 text-left">Flagged</th>
                <th className="px-3 py-3 text-left">Item</th>
                <th className="px-3 py-3 text-left">Reason / by</th>
                <th className="px-3 py-3 text-left">Prompt</th>
                <th className="px-3 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr data-testid={`flag-row-${r.generation_id}`} key={r.generation_id} className={selected.has(r.generation_id) ? 'bg-orange-50/40' : ''}>
                  <td className="px-3 py-3 align-top">
                    <input type="checkbox" data-testid={`flag-check-${r.generation_id}`} checked={selected.has(r.generation_id)} onChange={() => toggle(r.generation_id)} />
                  </td>
                  <td className="px-3 py-3 align-top">
                    <AuthedImage src={mediaPhotoUrl(r.item_id)} alt="original" className="w-16 h-16 object-cover rounded-lg bg-gray-100" />
                  </td>
                  <td className="px-3 py-3 align-top">
                    <AuthedImage src={mediaGeneratedUrl(r.generation_id)} alt="flagged" className="w-16 h-16 object-cover rounded-lg bg-gray-100 ring-2 ring-amber-300" />
                  </td>
                  <td className="px-3 py-3 align-top">
                    <p className="font-medium text-gray-900">{r.name}</p>
                    <p className="text-xs text-gray-500">{r.restaurant_name} · {r.category} · v{r.version}</p>
                  </td>
                  <td className="px-3 py-3 align-top max-w-[240px]">
                    <p className="text-gray-800">{r.flag_reason || <span className="text-gray-400">—</span>}</p>
                    <p className="text-xs text-gray-400">{r.reviewed_by || 'unknown'} · {r.reviewed_at || ''}</p>
                  </td>
                  <td className="px-3 py-3 align-top max-w-[320px]">
                    <PromptCell prompt={r.prompt} generationId={r.generation_id} />
                  </td>
                  <td className="px-3 py-3 align-top">
                    {r.regen_job_id ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                        regenerating · job #{r.regen_job_id} ({r.regen_job_status || '—'})
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                        <Flag className="h-3 w-3 fill-current" /> waiting
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showRegen && (
        <RegenModal
          rows={rows.filter((r) => selected.has(r.generation_id))}
          models={models}
          onClose={() => setShowRegen(false)}
          onLaunched={(d) => {
            setShowRegen(false)
            setToast({ kind: 'ok', msg: `Queued ${d.jobs.length} job(s): ${d.jobs.map((j) => '#' + j.job_id).join(', ')}` })
            load()
          }}
        />
      )}
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-semibold text-gray-900 mt-0.5">{value ?? '—'}</p>
    </div>
  )
}

function PromptCell({ prompt, generationId }) {
  if (!prompt) return <span className="text-xs text-gray-400">—</span>
  return (
    <details>
      <summary data-testid={`flag-prompt-${generationId}`} className="cursor-pointer text-xs text-orange-600 hover:underline list-none">
        View prompt
      </summary>
      <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed text-gray-600 bg-gray-50 rounded p-2">
        {prompt}
      </pre>
    </details>
  )
}

function RegenModal({ rows, models, onClose, onLaunched }) {
  const count = rows.length
  const generationIds = rows.map((r) => r.generation_id)
  const single = rows.length === 1 ? rows[0] : null
  const originalPrompt = single?.prompt || ''
  const [prompt, setPrompt] = useState(originalPrompt)
  const [model, setModel] = useState('')
  const [aspect, setAspect] = useState('')
  const [mode, setMode] = useState('batch')
  const [chunkSize, setChunkSize] = useState(50)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const perImage = mode === 'batch' ? 0.0195 : 0.039
  const estimate = (count * perImage).toFixed(4)
  const promptChanged = single && prompt !== originalPrompt

  const launch = async () => {
    setBusy(true); setError(null)
    try {
      const r = await regenerateFlags({
        generation_ids: generationIds,
        prompt: prompt.trim() || null,
        model: model || null,
        aspect_ratio: aspect || null,
        mode,
        chunk_size: Number(chunkSize) || 50,
      })
      onLaunched(r.data)
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to queue')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="regen-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Regenerate {count} flagged photo(s)</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Prompt{' '}
                <span className="text-xs font-normal text-gray-400">
                  {single ? '(original — edit to change)' : '(optional override)'}
                </span>
              </label>
              {single && promptChanged && (
                <button
                  data-testid="regen-reset-original"
                  type="button"
                  onClick={() => setPrompt(originalPrompt)}
                  className="text-xs text-orange-600 hover:underline"
                >
                  Reset to original
                </button>
              )}
            </div>
            <textarea
              data-testid="regen-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={8}
              placeholder={single ? 'Original prompt' : "Leave empty to reuse each flagged image's own prompt"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono"
            />
            {!single && (
              <p className="text-xs text-gray-400 mt-1">
                {count} images selected. Leave empty to reuse each image's own prompt; typing a
                prompt overrides all of them.
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              The reference image/attachment used originally is always reused and can’t be changed.
            </p>
            {!single && (
              <details data-testid="regen-originals" className="mt-2">
                <summary className="cursor-pointer text-xs text-orange-600 hover:underline">
                  Show original prompts ({count})
                </summary>
                <div className="mt-2 space-y-2 max-h-40 overflow-auto">
                  {rows.map((r) => (
                    <div key={r.generation_id} className="rounded bg-gray-50 p-2">
                      <p className="text-[11px] font-medium text-gray-700">{r.name} · v{r.version}</p>
                      <pre className="whitespace-pre-wrap break-words text-[11px] text-gray-600 mt-1">
                        {r.prompt || '—'}
                      </pre>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <select value={model} onChange={(e) => setModel(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Same as original</option>
                {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Aspect ratio</label>
              <select value={aspect} onChange={(e) => setAspect(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Same as original</option>
                {['1:1', '4:5', '3:4', '16:9'].map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="batch">Batch (cheap, slow)</option>
                <option value="realtime">Realtime (fast, 2× cost)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chunk size</label>
              <input type="number" value={chunkSize} onChange={(e) => setChunkSize(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600 flex items-center justify-between">
            <span>Estimated cost</span>
            <span className="font-semibold text-gray-900">${estimate}</span>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200">Cancel</button>
          <button data-testid="regen-launch" onClick={launch} disabled={busy}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Queue regeneration
          </button>
        </div>
      </div>
    </div>
  )
}
